import React, { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChannelMessageAttachmentType,
  ChannelViewClinicPetFragment,
  ClinicUserClinicFragment,
  GetMessageComposerAttachmentFormTemplateDocument,
} from 'shared/types/graphql';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { AvatarGroupSelect } from 'shared/components/Avatars/AvatarGroupSelect';
import { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import ChatMediaPreview from '../../../Modals/ChatMediaPreview';
import InvoiceAttachmentInput from './Invoice/InvoiceAttachmentInput';
import EditFeeItem from './Invoice/EditFeeItem';
import { useApolloClient } from '@apollo/client';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { IChannelDraft } from 'pages/Conversations/interfaces/IChannelDraft';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePaymentWritebackConfig from 'shared/hooks/usePaymentWritebackConfig';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { useBitwerxData } from 'shared/providers/IntegrationsProvider';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { Box, Flex, TextInput, Button, Icon, Text, VStack, Alert, Card, Heading, HStack } from '@televet/kibble-ui';

interface IAttachmentProps {
  attachment: IAttachmentOption;
  linkedPets: ChannelViewClinicPetFragment[];
  clinic?: ClinicUserClinicFragment | null;
  inputTextareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  channelDraft: IChannelDraft;
  setChannelDraft: (channelDraft: Partial<IChannelDraft>) => void;
  invoiceAmountInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onAttachmentChange: (attachment: IAttachmentOption) => void;
  onAttachmentRemove: (attachment: IAttachmentOption) => void;
  setPreviewData: (fileUrl: string, attachmentType: ChannelMessageAttachmentType, fileName: string) => void;
  setCurrentInvoiceId: (invoiceId: string) => void;
  setAttachmentFeePercent: (percent: number) => void;
  setHasPotentialBenefits: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLinkPetToFormModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedFormAttachment: React.Dispatch<React.SetStateAction<IAttachmentOption | undefined>>;
  onOpenFormFieldModalOpen: () => void;
}

const MessageComposerAttachment = ({
  attachment,
  linkedPets,
  clinic,
  inputTextareaRef,
  invoiceAmountInputRef,
  onAttachmentChange,
  onAttachmentRemove,
  setPreviewData,
  setCurrentInvoiceId,
  setAttachmentFeePercent,
  setHasPotentialBenefits,
  setIsLinkPetToFormModalOpen,
  setSelectedFormAttachment,
  onOpenFormFieldModalOpen,
}: IAttachmentProps): JSX.Element => {
  const clinicSettings = clinic?.clinicSetting;
  const { hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { isMobile } = useResolutionProvider();
  const { needsBitwerxForPaymentWritebacks } = useBitwerxData();
  const [isEditingFee, setIsEditingFee] = useState(false);
  const [feeValidationWarning, setFeeValidationWarning] = useState<string | undefined>(undefined);
  const {
    file,
    value: { attachmentType, filename, entityId },
    text,
  } = attachment;
  const isMedia =
    attachmentType === ChannelMessageAttachmentType.Image || attachmentType === ChannelMessageAttachmentType.Video;
  const apolloClient = useApolloClient();
  const {
    isComplete: writebackConfigComplete,
    isValid: writebackConfigValid,
    isClinicWritebackEnabled,
    isWritebackManualPaymentEnabled,
  } = usePaymentWritebackConfig();

  const isManualAmount = useMemo(
    () =>
      !attachment.invoiceIds?.length &&
      !attachment.isBalanceSelected &&
      parseFloat(attachment.invoiceAmount || '0') > 0,
    [attachment.isBalanceSelected, attachment.invoiceAmount, attachment.invoiceIds?.length],
  );

  // only show in writeback capable clinic, with PIMS client, and with manual amount (since writeback is assumed for invoice/balance)
  const showWritebackEnabledStatus = useMemo(
    () => isClinicWritebackEnabled && linkedPets.some((pet) => pet.pimsId) && isManualAmount,
    [isClinicWritebackEnabled, isManualAmount, linkedPets],
  );

  const { isFeatureEnabled } = useFeatureFlag();

  const fetchFormAttachment = useCallback(async () => {
    const result = await apolloClient.query({
      query: GetMessageComposerAttachmentFormTemplateDocument,
      variables: {
        where: {
          clinicId: {
            equals: clinic?.id,
          },
          id: {
            equals: entityId,
          },
        },
      },
      fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    });

    onAttachmentChange({
      ...attachment,
      text: result.data.findFirstFormTemplate.title,
      formTemplateContent: result.data.findFirstFormTemplate.content,
    });
  }, [apolloClient, attachment, clinic?.id, entityId, onAttachmentChange]);

  if (attachmentType === ChannelMessageAttachmentType.FormRequest && !text && clinic?.id) {
    fetchFormAttachment();
  }

  const removePetFromAttachment = (e: MouseEvent, option?: IOption): void => {
    e.stopPropagation();
    e.preventDefault();
    if (attachment.clinicPetIds?.includes(option?.value.id)) {
      attachment.clinicPetIds = attachment.clinicPetIds.filter((id: string) => id !== option?.value.id);
    }
    onAttachmentChange({
      ...attachment,
    });
  };

  const avatarGroupOptions = useMemo(() => {
    if (attachmentType !== ChannelMessageAttachmentType.FormRequest) return [];
    const petOptions = linkedPets.map((pet) => ({
      text: pet.name,
      value: {
        id: pet.id,
        firstName: pet.name,
      },
      isSelected: attachment.clinicPetIds?.includes(pet.id),
    }));
    return [...petOptions];
  }, [linkedPets, attachment, attachmentType]);

  useEffect(() => {
    if (
      attachment?.clinicPetIds === undefined &&
      linkedPets.length === 1 &&
      attachmentType === ChannelMessageAttachmentType.FormRequest
    ) {
      onAttachmentChange({
        ...attachment,
        clinicPetIds: [linkedPets[0].id],
      });
      setIsLinkPetToFormModalOpen(false);
    }
  }, [linkedPets, attachment, onAttachmentChange, setIsLinkPetToFormModalOpen, attachmentType]);
  /**
   * Set the default clientServiceFeePercent on the attachment if available
   * Dep is ignored to allow updating of attachment once to set the fee
   */
  useEffect(() => {
    if (clinicSettings?.paymentFeeConfig && clinicSettings?.hasCustomizableFees) {
      onAttachmentChange({
        ...attachment,
        clientServiceFeePercent: clinicSettings.paymentFeeConfig.onlineClientServiceFeePercent,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicSettings, onAttachmentChange, isFeatureEnabled]);

  /**
   * Formatted service fee percentage
   */
  const serviceFeePercentage = useMemo(() => {
    let percent = 0;
    if (attachment?.clientServiceFeePercent) {
      percent = attachment?.clientServiceFeePercent * 100;
    }
    setAttachmentFeePercent(percent);

    return percent.toFixed(2);
  }, [attachment, setAttachmentFeePercent]);

  /**
   * Calculate client service fee
   */
  const clientServiceFee = useMemo(() => {
    if (!attachment?.clientServiceFeePercent) return 0;
    const invoiceAmount = Number(attachment?.invoiceAmount);
    if (isNaN(invoiceAmount)) return '--.--';

    return (invoiceAmount * attachment.clientServiceFeePercent).toFixed(2);
  }, [attachment]);

  const showWritebackConfigurationWarning = useMemo(() => {
    // 1. is not on mobile since settings page is not exposed on mobile
    // 2. needs bitwerx OR has bitwerx but writeback config is incomplete OR writeback config is invalid
    // 3. clinic PIMS can support writebacks
    return (
      !isMobile &&
      (needsBitwerxForPaymentWritebacks || !writebackConfigComplete || !writebackConfigValid) &&
      isClinicWritebackEnabled
    );
  }, [
    writebackConfigComplete,
    writebackConfigValid,
    isMobile,
    needsBitwerxForPaymentWritebacks,
    isClinicWritebackEnabled,
  ]);

  const removeAttachment = useCallback(() => {
    onAttachmentRemove(attachment);
    setHasPotentialBenefits(false);
    if (attachment.isFillInTheBlankForm) {
      setSelectedFormAttachment(undefined);
    }
    inputTextareaRef?.current?.focus();
  }, [onAttachmentRemove, attachment, setHasPotentialBenefits, inputTextareaRef, setSelectedFormAttachment]);

  const handleEditFormClick = useCallback((): void => {
    setSelectedFormAttachment(attachment);
    onOpenFormFieldModalOpen();
  }, [attachment, onOpenFormFieldModalOpen, setSelectedFormAttachment]);

  const goToPaymentSettings = useCallback((): void => {
    window.location.href = `${RouteBasePaths.Settings}${SettingsRoutes.ClinicPayment}`;
  }, []);
  /**
   * Get the maximum allowed fee percentage
   */
  const clinicProcessingFeePercent = clinicSettings?.paymentFeeConfig?.onlineProcessingFeePercent;
  const maximumProcessingFeePercentage = clinicProcessingFeePercent ? (clinicProcessingFeePercent * 100).toFixed(2) : 0;

  const clientServiceFeeMax = Number(maximumProcessingFeePercentage);

  const [clientServiceFeePercent, setClientServiceFeePercent] = useState<string | undefined>(undefined);
  // Handle changing and validating the client service fee percent being entered
  const handleChangeClientServiceFeePercent = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (!e.target.value) {
        setClientServiceFeePercent(undefined);
        setFeeValidationWarning(undefined);
        return;
      }

      if (isNaN(Number(e.target.value))) return; // No special characters

      const [, decimal] = e.target.value.split('.');

      if (decimal && decimal.length > 2) return; // Only allow 2 decimal places

      const stringVal = e.target.value.replace(',', '');
      const numericVal = parseFloat(stringVal);

      if (numericVal < 0) {
        setClientServiceFeePercent(undefined);
        setFeeValidationWarning(undefined);
        return;
      } else if (clientServiceFeeMax && numericVal > clientServiceFeeMax) {
        setFeeValidationWarning(
          `Please enter an amount less than your clinic service fee of ${maximumProcessingFeePercentage}%`,
        );
        setClientServiceFeePercent(clientServiceFeeMax.toFixed(2));
        return;
      } else {
        setClientServiceFeePercent(stringVal);
        setFeeValidationWarning(undefined);
      }
    },
    [clientServiceFeeMax, maximumProcessingFeePercentage],
  );

  const renderTypeIcon = (attachmentType: ChannelMessageAttachmentType): JSX.Element | null => {
    if (attachmentType === ChannelMessageAttachmentType.Image || attachmentType === ChannelMessageAttachmentType.Video)
      return (
        <Flex alignItems="center" justifyContent="center" width={10} height="100%">
          <Icon name="image" />
        </Flex>
      );
    if (attachmentType === ChannelMessageAttachmentType.FormRequest)
      return (
        <Flex alignItems="center" justifyContent="center" width={10} height="100%">
          <Icon name="form" />
        </Flex>
      );

    if (attachmentType === ChannelMessageAttachmentType.File) {
      const fileType = file?.type;
      if (fileType?.includes('pdf')) {
        return (
          <Flex alignItems="center" justifyContent="center" width={10} height="100%">
            <Icon name="form" />
          </Flex>
        );
      } else if (fileType?.includes('word')) {
        return (
          <Flex alignItems="center" justifyContent="center" width={10} height="100%">
            <Icon name="form" />
          </Flex>
        );
      } else {
        return (
          <Flex alignItems="center" justifyContent="center" width={10} height="100%">
            <Icon name="paperclip" />
          </Flex>
        );
      }
    }
    return null;
  };

  const renderAttachment = (): JSX.Element => {
    switch (attachmentType) {
      case ChannelMessageAttachmentType.Invoice:
        return (
          <VStack className="InvoiceAttachment__Container" align="flex-start" spacing={0}>
            {showWritebackConfigurationWarning && (
              <Alert
                className="InvoiceAttachment__Warning"
                status="warning"
                {...(hasAdminRoleAtCurrentClinic && {
                  buttonProps: {
                    buttonText: 'View Payment Settings',
                    buttonPosition: 'bottomLeft',
                    onClick: goToPaymentSettings,
                  },
                })}
                hideCloseButton
                description={
                  hasAdminRoleAtCurrentClinic
                    ? 'Ensure that your payment settings are configured properly.'
                    : 'Please contact an administrator to fix invalid payments configuration for writebacks to succeed.'
                }
                title="Payment Configuration Incomplete"
              />
            )}
            <Card
              className="InvoiceAttachment__Attachment"
              size="xs"
              data-testid="invoice-attachment-container"
              w="300px"
            >
              <VStack align="flex-start" spacing={1} w="100%">
                <HStack className="InvoiceAttachment__Attachment-Header" justify="space-between" w="100%">
                  <Heading size="sm">Payment Amount</Heading>
                  <Button onClick={removeAttachment} iconName="close" variant="ghostNeutral" size="xs" />
                </HStack>
                <InvoiceAttachmentInput
                  attachment={attachment}
                  onAttachmentChange={onAttachmentChange}
                  onAttachmentRemove={removeAttachment}
                  setCurrentInvoiceId={setCurrentInvoiceId}
                  invoiceAmountInputRef={invoiceAmountInputRef}
                />
                {showWritebackEnabledStatus && (
                  <HStack
                    className="InvoiceAttachment__Attachment-Details"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                  >
                    {isWritebackManualPaymentEnabled && <Icon size="xs" name="transfer" />}
                    <Text size="sm" variant="subtle" ml={2}>{`Writeback to account ${
                      isWritebackManualPaymentEnabled ? 'enabled' : 'disabled'
                    }`}</Text>
                  </HStack>
                )}
              </VStack>
            </Card>
            {clinicSettings?.hasCustomizableFees && (
              <VStack className="InvoiceAttachment__Footer" align="stretch" spacing={1}>
                {!!feeValidationWarning && (
                  <Text noOfLines={2} maxWidth={60} mr={4} size="xs" variant="danger">
                    {feeValidationWarning}
                  </Text>
                )}
                <EditFeeItem
                  serviceFeePercentage={serviceFeePercentage}
                  clientServiceFee={clientServiceFee}
                  callToActionFunction={(): void => setIsEditingFee(true)}
                />
                {isEditingFee && (
                  <Flex direction="row" alignItems="center" paddingStart={2}>
                    <TextInput
                      type="number"
                      size="sm"
                      maxWidth={28}
                      onChange={handleChangeClientServiceFeePercent}
                      value={
                        clientServiceFeePercent && clientServiceFeeMax
                          ? parseFloat(clientServiceFeePercent) > clientServiceFeeMax
                            ? ''
                            : clientServiceFeePercent
                          : ''
                      }
                      rightElement={<Text>%</Text>}
                      placeholder={clientServiceFeePercent ? `${clientServiceFeePercent}` : '0.00'}
                    />
                    <HStack>
                      <Button
                        variant="tertiary"
                        onClick={(): void => {
                          setFeeValidationWarning(undefined);
                          setIsEditingFee(false);
                        }}
                        ml={2}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={(): void => {
                          setIsEditingFee(false);
                          setFeeValidationWarning(undefined);
                          onAttachmentChange({
                            ...attachment,
                            clientServiceFeePercent: Number(clientServiceFeePercent) / 100,
                          });
                        }}
                        ml={2}
                      >
                        Save
                      </Button>
                    </HStack>
                  </Flex>
                )}
              </VStack>
            )}
          </VStack>
        );
      case ChannelMessageAttachmentType.CallRequest:
        return (
          <Card
            className="CallRequestAttachment__Container"
            size="xs"
            data-testid="call-request-attachment-container"
            w="300px"
          >
            <HStack className="CallRequestAttachment__Attachment-Header" justify="space-between" w="100%">
              <HStack>
                <Icon name="videoCamera" />
                <Heading size="sm">Video Call Request</Heading>
              </HStack>
              <Button onClick={removeAttachment} iconName="close" variant="ghostNeutral" size="sm" />
            </HStack>
          </Card>
        );
      case ChannelMessageAttachmentType.File:
      default:
        return (
          <Card className="FileAttachment__Container" size="xs" data-testid="file-attachment-container" w="300px">
            <HStack className="FileAttachment__Attachment-Header" justify="space-between" w="100%">
              <HStack>
                {file ? (
                  <ChatMediaPreview
                    attachment={attachment}
                    onAttachmentRemove={onAttachmentRemove}
                    key={filename}
                    stagedFile={file}
                    isMedia={isMedia}
                    renderTypeIcon={(): JSX.Element | null => renderTypeIcon(attachmentType)}
                    setPreviewData={setPreviewData}
                  />
                ) : (
                  renderTypeIcon(ChannelMessageAttachmentType.FormRequest)
                )}
                <Heading isTruncated size="sm">
                  {text || filename || ''}
                </Heading>
              </HStack>
              {attachmentType === ChannelMessageAttachmentType.FormRequest && linkedPets.length !== 0 && (
                <Box onClick={(): void => setIsLinkPetToFormModalOpen(true)}>
                  <AvatarGroupSelect
                    isAddingAvatar={true}
                    hideDropdownOption={true}
                    options={avatarGroupOptions}
                    avatarSize="sm"
                    onToggle={removePetFromAttachment}
                    popoverPlacement={PopoverPlacement.TopStart}
                    addButtonTooltip="Link to patient (optional)"
                  />
                </Box>
              )}
              {attachmentType === ChannelMessageAttachmentType.FormRequest && attachment.isFillInTheBlankForm && (
                <Button variant="secondarySubtle" size="sm" onClick={handleEditFormClick}>
                  Edit
                </Button>
              )}
              <Button onClick={removeAttachment} iconName="close" variant="ghostNeutral" size="sm" />
            </HStack>
          </Card>
        );
    }
  };

  return <VStack>{renderAttachment()}</VStack>;
};

export default MessageComposerAttachment;
